import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import snsWebSdk, { type SnsWebSdk } from '@sumsub/websdk';
import { AppHeader } from '../components/AppHeader';
import { ApiService } from '../services/apiService';
import { buildSumsubWebSdkConf } from '../utils/sumsubWebSdkConf';
import './SumsubKyc.css';
import { CardState } from '../types/cardTypes';
import { getSumsubRejectionUserMessage } from '../utils/sumsubKycMessages';
// @mcc-dev-preview:import
import { parseDevState } from '../dev/devPreview';

// @mcc-dev-preview:start kyc-verification-dev-state
const KYC_VERIFICATION_DEV_STATES = ['loading', 'ready', 'error'] as const;
const DEV_PREVIEW_ACCESS_TOKEN = 'dev-preview-sumsub-token';
// @mcc-dev-preview:end kyc-verification-dev-state

const SUMSUB_CONTAINER_ID = 'sumsub-websdk-container';

/** After KYC approval, go straight to activation — not /card/:id (CardRouter sends KYCPENDING back to KYC). */
const kycCompleteRoute = (id: string) =>
  `/redirect-to-activation?cardId=${encodeURIComponent(id)}`;

const SumsubKyc: React.FC = () => {
  const [searchParams] = useSearchParams();
  const cardId = searchParams.get('cardId');
  const customizationNameParam = searchParams.get('customizationName');
  const navigate = useNavigate();
  // @mcc-dev-preview:start kyc-verification-dev-state
  const devState = parseDevState(searchParams, KYC_VERIFICATION_DEV_STATES);
  // @mcc-dev-preview:end kyc-verification-dev-state

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sdkError, setSdkError] = useState<string | null>(null);

  const fetchToken = useCallback(async (): Promise<string> => {
    if (!cardId) {
      throw new Error('cardId is required');
    }
    const res = await ApiService.getSumsubAccessToken(cardId);
    if (!res.success || !res.data?.token) {
      throw new Error(res.error || 'Could not start verification');
    }
    return res.data.token;
  }, [cardId]);

  // @mcc-dev-preview:start kyc-verification-dev-state
  useEffect(() => {
    if (!devState) return;

    switch (devState) {
      case 'loading':
        setLoading(true);
        setError(null);
        setAccessToken(null);
        break;
      case 'ready':
        setLoading(false);
        setError(null);
        setAccessToken(DEV_PREVIEW_ACCESS_TOKEN);
        break;
      case 'error':
        setLoading(false);
        setAccessToken(null);
        setError(
          searchParams.get('errorMessage') ??
            'Could not start verification. Please try again later.'
        );
        break;
      default:
        break;
    }
  }, [devState, searchParams]);
  // @mcc-dev-preview:end kyc-verification-dev-state

  useEffect(() => {
    // @mcc-dev-preview:start kyc-verification-dev-state
    if (devState) return;
    // @mcc-dev-preview:end kyc-verification-dev-state

    if (!cardId?.trim()) {
      setError('A card reference is required to start verification. Use the link from your card or return to verification.');
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        // KYCPending: on first arrival show the blocking SDK, but on refresh/browse again
        // redirect to the "KYC In Process" screen — only when the card is actually KYCPENDING.
        // (Stale session flags from failed token loads caused /kyc-verification ↔ /kyc-in-process loops.)
        const sessionKey = `mcc_kyc_pending_seen_${cardId}`;

        const seen = sessionStorage.getItem(sessionKey);
        // #region agent log
        fetch('http://127.0.0.1:7361/ingest/ac35d597-8d02-4005-931f-a2b834303e25',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'55344c'},body:JSON.stringify({sessionId:'55344c',location:'SumsubKyc.tsx:session-flag',message:'Session pending flag check',data:{cardId,seen:Boolean(seen)},timestamp:Date.now(),hypothesisId:'B'})}).catch(()=>{});
        // #endregion
        if (seen) {
          let inKycPending = false;
          try {
            const card = await ApiService.getCardByCardId(cardId);
            if (card?.state === CardState.KYCPENDING) {
              inKycPending = true;
            }
          } catch {
            // Cannot confirm state; clear stale flag and continue to token flow.
          }
          if (inKycPending) {
            navigate(`/kyc-in-process?cardId=${encodeURIComponent(cardId)}`);
            return;
          }
          sessionStorage.removeItem(sessionKey);
        }

        const early = await ApiService.getSumsubKycStatus();
        // #region agent log
        fetch('http://127.0.0.1:7361/ingest/ac35d597-8d02-4005-931f-a2b834303e25',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'55344c'},body:JSON.stringify({sessionId:'55344c',location:'SumsubKyc.tsx:early-kyc-status',message:'Early kyc-status check',data:{cardId,readyForActivation:early.data?.readyForActivation,sumsubKycStatus:early.data?.sumsubKycStatus,success:early.success,pathname:window.location.pathname},timestamp:Date.now(),hypothesisId:'A,C'})}).catch(()=>{});
        // #endregion
        if (!cancelled && early.success && early.data?.readyForActivation) {
          const target = kycCompleteRoute(cardId);
          // #region agent log
          fetch('http://127.0.0.1:7361/ingest/ac35d597-8d02-4005-931f-a2b834303e25',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'55344c'},body:JSON.stringify({sessionId:'55344c',location:'SumsubKyc.tsx:early-redirect',message:'Navigating after early readyForActivation',data:{cardId,target,runId:'post-fix'},timestamp:Date.now(),hypothesisId:'A'})}).catch(()=>{});
          // #endregion
          navigate(target);
          return;
        }

        const t = await fetchToken();
        if (!cancelled) {
          setAccessToken(t);
          sessionStorage.setItem(sessionKey, '1');
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load verification');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cardId, fetchToken, navigate, devState]);

  const expirationHandler = useCallback(async () => fetchToken(), [fetchToken]);

  const sumsubTestEnv = process.env.REACT_APP_SUMSUB_TEST_ENV === 'true';

  useEffect(() => {
    // @mcc-dev-preview:start kyc-verification-dev-state
    if (devState) return undefined;
    // @mcc-dev-preview:end kyc-verification-dev-state
    if (!accessToken) return undefined;

    let instance: SnsWebSdk | null = null;

    const builder = snsWebSdk.init(accessToken, expirationHandler);
    if (sumsubTestEnv) {
      builder.onTestEnv();
    }

    instance = builder
      .withConf(
        buildSumsubWebSdkConf({
          lang: 'en',
          theme: 'light',
          customizationNameOverride: customizationNameParam,
        })
      )
      .withOptions({ addViewportTag: false, adaptIframeHeight: true })
      .on('idCheck.onError', (err) => {
        console.error('Sumsub WebSDK error:', err);
        setSdkError(err.reason || err.error || 'Verification UI error');
      })
      .build();

    instance.launch(`#${SUMSUB_CONTAINER_ID}`);

    return () => {
      instance?.destroy();
      instance = null;
    };
  }, [accessToken, expirationHandler, sumsubTestEnv, customizationNameParam, devState]);

  useEffect(() => {
    // @mcc-dev-preview:start kyc-verification-dev-state
    if (devState) return undefined;
    // @mcc-dev-preview:end kyc-verification-dev-state
    if (!accessToken || !cardId?.trim()) return undefined;

    let stopped = false;
    const id = window.setInterval(() => {
      void (async () => {
        if (stopped) return;
        const status = await ApiService.getSumsubKycStatus();
        if (stopped || !status.success || !status.data) return;

        if (status.data.readyForActivation) {
          const target = kycCompleteRoute(cardId);
          // #region agent log
          fetch('http://127.0.0.1:7361/ingest/ac35d597-8d02-4005-931f-a2b834303e25',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'55344c'},body:JSON.stringify({sessionId:'55344c',location:'SumsubKyc.tsx:poll-redirect',message:'Navigating after poll readyForActivation',data:{cardId,target,runId:'post-fix'},timestamp:Date.now(),hypothesisId:'A,C'})}).catch(()=>{});
          // #endregion
          stopped = true;
          window.clearInterval(id);
          navigate(target);
          return;
        }

        if (status.data.sumsubKycStatus === 'rejected') {
          stopped = true;
          window.clearInterval(id);

          // If the backend moved the card to CardNotUsable, show the applicant-facing rejection screen.
          // Otherwise (e.g. temporary RETRY), keep the error on this page.
          if (status.data.sumsubReviewAnswer === 'RED') {
            try {
              const card = await ApiService.getCardByCardId(cardId);
              if (card?.state === CardState.CARD_NOT_USABLE) {
                navigate(`/card-not-usable?cardId=${encodeURIComponent(cardId)}`);
                return;
              }
            } catch {
              // Ignore and fall back to inline error.
            }
          }

          setError(
            getSumsubRejectionUserMessage({
              sumsubReviewAnswer: status.data.sumsubReviewAnswer,
              sumsubReviewRejectType: status.data.sumsubReviewRejectType,
              sumsubRejectLabels: status.data.sumsubRejectLabels,
              moderationComment: status.data.moderationComment
            })
          );
          return;
        }

        // Banxa is only required at the very end of the flow (when creating the Banxa transaction).
        // Do not block applicant progression here.
        // We keep the UI decision based on Sumsub results only.
      })();
    }, 4000);
    void (async () => {
      if (stopped) return;
      const status = await ApiService.getSumsubKycStatus();
      if (stopped || !status.success || !status.data) return;
      if (status.data.readyForActivation) {
        stopped = true;
        window.clearInterval(id);
        navigate(kycCompleteRoute(cardId));
      }
    })();

    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [accessToken, cardId, navigate, devState]);

  return (
    <div className="sumsub-kyc-page ev-page ev-page-enter">
      <AppHeader />
      <main className="sumsub-kyc-main">
        <div className="sumsub-kyc-card ev-shell-card ev-animate-in">
          <h1 className="ev-display-headline" style={{ color: 'var(--ev-text-on-card)', fontSize: '24px' }}>Verify your identity</h1>
          <p className="ev-subtext">
            Complete the steps below. This page updates automatically once verification and partner registration are complete.
          </p>

          {loading && <p className="ev-subtext">Starting verification…</p>}
          {error && (
            <div className="sumsub-kyc-error" role="alert">
              <p>{error}</p>
              <p className="ev-subtext" style={{ marginTop: '0.75rem' }}>
                <button type="button" className="back-button ev-btn-secondary" onClick={() => navigate(-1)}>
                  Go back
                </button>
              </p>
            </div>
          )}
          {sdkError && (
            <div className="sumsub-kyc-error" role="alert">
              <p>{sdkError}</p>
            </div>
          )}

          {accessToken && !error && (
            <div className="sumsub-kyc-sdk-wrap">
              {/* @mcc-dev-preview:start kyc-verification-dev-state */}
              {devState === 'ready' && (
                <div
                  className="sumsub-kyc-dev-preview-placeholder"
                  style={{
                    minHeight: 320,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed var(--ev-card-border)',
                    borderRadius: 'var(--ev-card-radius)',
                    color: 'var(--ev-text-muted)',
                    marginBottom: '1rem',
                  }}
                >
                  Sumsub WebSDK preview (dev)
                </div>
              )}
              {/* @mcc-dev-preview:end kyc-verification-dev-state */}
              <div id={SUMSUB_CONTAINER_ID} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SumsubKyc;
